import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useStore } from 'react-redux'
import { useLatest } from 'react-use'
import { ROWS_PER_PAGE_OPTIONS } from '../../../../components/DataTable/paginationOptions'
import { type CrossClientQueryResult, CrossClientQueryResultStatuses } from '../../../../models/CrossClient'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { type RootState } from '../../../../store/store'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useChangeCrossClientQueryResultMatchMutation } from '../../../../store/crossClient/crossClient'
import {
  holdCrossClientResultBuckets,
  releaseCrossClientResultBuckets,
  selectCrossClientClients,
  selectCrossClientResultBuckets,
} from '../../../../store/crossClient/crossClientResults'
import { executionHasNothingResolvable } from '../../../../store/crossClient/utils'
import { addToast } from '../../../../store/toast/toastSlice'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { useCrossClientResultsRefresh } from '../useCrossClientResultsRefresh'
import { CCQ_CARD_ORDER, CcqCards, cardForResult, moveInBuckets, sumCounts, type CcqCard } from './ccqResultView'
import { ccqMoveFailed, ccqMovedToast } from './ccqRunCopy'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { useFetchMissingCrossClientClients } from '../hooks'
import { useCcqDeselectUnlistedOnActions, useCcqPanelResult, useCcqPreviews } from './useCcqCardResults'
import { CcqListCursorContext, useCcqListCursor, type CcqCursorState, type CcqListCursor, type CcqSelectedClient } from './useCcqListCursor'
import { useCcqRun } from './useCcqRun'
import { useRetryLater } from './useRetryLater'
import {
  CCQ_PANEL_EXIT_MS,
  CcqActionFilters,
  CcqWorkspaceContext,
  hasActionsFor,
  type CcqActionFilter,
  type CcqChangeableMatch,
  type CcqWorkspace,
} from './useCcqWorkspace'

// An empty card shows closed regardless, so MATCHED opens by itself once its first client lands.
const DEFAULT_OPEN_CARD: CcqCard = CcqCards.MATCHED

const expandedFor = (openCard: CcqCard | null): Record<CcqCard, boolean> =>
  Object.fromEntries(CCQ_CARD_ORDER.map((card) => [card, card === openCard])) as Record<CcqCard, boolean>

const LIST_PARAM = 'list'
const CLIENT_PARAM = 'client'
const CLIENT_CARD_PARAM = 'clientCard'
const PAGE_PARAM = 'page'
const SIZE_PARAM = 'size'
const SEARCH_PARAM = 'search'
const ACTIONS_PARAM = 'actions'

const DEFAULT_PAGE_SIZE = ROWS_PER_PAGE_OPTIONS[1]

const MOVED_TOAST_MS = 5000

// A client the advisor moved: the card the check put them in, the card they're in now, and every card they've left, whose
// rows go at once, ahead of the next read.
interface CcqManualMove {
  original: CcqCard
  current: CcqCard
  left: CcqCard[]
}

const withoutKey = <T,>(record: Record<string, T>, key: string): Record<string, T> =>
  Object.fromEntries(Object.entries(record).filter(([each]) => each !== key))

// A card's header, for focus to fall back on when what opened the panel has gone.
const ccqCardHeadSelector = (card: CcqCard) => `[data-ccq-card-head='${card}']`

const cardFromParam = (value: string | null): CcqCard | null => CCQ_CARD_ORDER.find((card) => card === value) ?? null

const pageFromParam = (value: string | null): number => {
  const page = Number(value)
  return Number.isInteger(page) && page > 1 ? page : 1
}

const pageSizeFromParam = (value: string | null): number => ROWS_PER_PAGE_OPTIONS.find((size) => String(size) === value) ?? DEFAULT_PAGE_SIZE

const actionFilterFromParam = (value: string | null): CcqActionFilter =>
  Object.values(CcqActionFilters).find((filter) => filter === value) ?? CcqActionFilters.ALL

const cursorStateFromParams = (searchParams: URLSearchParams): CcqCursorState => {
  const listCard = cardFromParam(searchParams.get(LIST_PARAM))
  const resultId = searchParams.get(CLIENT_PARAM)
  const clientCard = cardFromParam(searchParams.get(CLIENT_CARD_PARAM)) ?? listCard
  return {
    list:
      listCard === null
        ? null
        : {
            card: listCard,
            page: pageFromParam(searchParams.get(PAGE_PARAM)),
            search: searchParams.get(SEARCH_PARAM) ?? '',
            filter: actionFilterFromParam(searchParams.get(ACTIONS_PARAM)),
          },
    size: listCard === null ? DEFAULT_PAGE_SIZE : pageSizeFromParam(searchParams.get(SIZE_PARAM)),
    selected: resultId === null || clientCard === null ? null : { resultId, card: clientCard },
  }
}

const paramsFromCursorState = ({ list, size, selected }: CcqCursorState): Record<string, string | null> => ({
  [LIST_PARAM]: list?.card ?? null,
  [CLIENT_PARAM]: selected?.resultId ?? null,
  [CLIENT_CARD_PARAM]: selected === null || selected.card === list?.card ? null : selected.card,
  [PAGE_PARAM]: list !== null && list.page > 1 ? String(list.page) : null,
  [SIZE_PARAM]: list !== null && size !== DEFAULT_PAGE_SIZE ? String(size) : null,
  [SEARCH_PARAM]: list !== null && list.search !== '' ? list.search : null,
  [ACTIONS_PARAM]: list !== null && list.filter !== CcqActionFilters.ALL ? list.filter : null,
})

export const CcqWorkspaceProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { execution, isDone, counts } = useCcqRun()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const [restored] = useState(() => cursorStateFromParams(searchParams))

  const [openCard, setOpenCard] = useState<CcqCard | null>(restored.list?.card ?? DEFAULT_OPEN_CARD)
  const expanded = useMemo(() => expandedFor(openCard), [openCard])

  // Once at done, unless the advisor picked a card, open the card that explains an empty result.
  // Waits for the counts: a finished search opens before its first buckets load.
  const [hasSettledOpenCard, setHasSettledOpenCard] = useState(false)
  if (isDone && !hasSettledOpenCard && sumCounts(counts) > 0) {
    setHasSettledOpenCard(true)
    const isEveryOtherCardEmpty = [CcqCards.MATCHED, CcqCards.CLOSER, CcqCards.NOT_IN_RECORDS].every((card) => counts[card] === 0)
    const explainingCard =
      executionHasNothingResolvable(execution) && counts.notInRecords > 0
        ? CcqCards.NOT_IN_RECORDS
        : isEveryOtherCardEmpty
          ? [CcqCards.NOT_MATCHED, CcqCards.MISSING_DATA].find((card) => counts[card] > 0)
          : undefined
    if (openCard === DEFAULT_OPEN_CARD && explainingCard !== undefined) {
      setOpenCard(explainingCard)
    }
  }

  const [settleToken, setSettleToken] = useState(0)
  const {
    previews: readPreviews,
    loaded: previewsLoaded,
    failed: previewsFailed,
    reads: previewReads,
    retry: retryPreviews,
  } = useCcqPreviews(counts, isDone, settleToken)
  const [moves, setMoves] = useState<Record<string, CcqManualMove>>({})
  const isMovedOut = useCallback((resultId: string, card: CcqCard) => moves[resultId]?.left.includes(card) === true, [moves])
  const previews = useMemo(
    () =>
      Object.fromEntries(CCQ_CARD_ORDER.map((card) => [card, readPreviews[card].filter((result) => !isMovedOut(result.id, card))])) as Record<
        CcqCard,
        CrossClientQueryResult[]
      >,
    [readPreviews, isMovedOut],
  )
  const cursor = useCcqListCursor({ previews, counts, isMovedOut, previewReads, restored })

  const [isPanelClosing, setIsPanelClosing] = useState(false)
  // The client the panel keeps showing while it slides out; the cursor has already let go of it.
  const [leaving, setLeaving] = useState<CcqSelectedClient | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const panelOpenerRef = useRef<HTMLElement | null>(null)
  const panelFocusRequestRef = useRef(false)
  const listCloseFocusRef = useRef<CcqCard | null>(null)
  const [selection, setSelection] = useState<string[]>([])

  // The card a list opens on stays open when the list closes, so its See all is there to take focus back; Next past the Preview opens one too.
  const [openListCard, setOpenListCard] = useState(cursor.list.card)
  if (openListCard !== cursor.list.card) {
    setOpenListCard(cursor.list.card)
    if (cursor.list.card !== null) {
      setOpenCard(cursor.list.card)
    }
  }

  // A selection made under another search, filter or list would aim the footer's actions at rows no longer shown.
  const [selectionListKey, setSelectionListKey] = useState(cursor.listKey)
  if (selectionListKey !== cursor.listKey) {
    setSelectionListKey(cursor.listKey)
    setSelection([])
  }

  const panel = cursor.details.selected ?? leaving
  const { result: panelResult, isMissing: isPanelResultMissing } = useCcqPanelResult(panel?.resultId ?? null, panel?.row ?? null)
  // A panel restored from a stale link, whose client is gone or unreadable, would show an empty rail that can't be closed.
  // It closes straight away: with no client to show, there's no exit to play, Preview to refresh or focus to hand back.
  const { selected, close: closeInCursor } = cursor.details
  useEffect(() => {
    if (isPanelResultMissing && selected !== null) {
      closeInCursor()
    }
  }, [isPanelResultMissing, selected, closeInCursor])

  // As the old details modal reported it: 'list' when opened from a row, 'detail' when stepped to.
  const { trackingClient } = useTracking()
  const trackedViewRef = useRef<string | null>(null)
  const viewedClientId = panelResult !== null && panelResult.id === panel?.resultId ? panelResult.clientId : null
  const viewSource = cursor.details.lastMove.direction === 0 ? 'list' : 'detail'
  useEffect(() => {
    if (panel === null) {
      trackedViewRef.current = null
      return
    }
    if (viewedClientId === null || trackedViewRef.current === panel.resultId) {
      return
    }
    trackedViewRef.current = panel.resultId
    trackingClient.track({
      eventName: TrackEvent.X_CLIENT_QUERY_VIEW_MATCH,
      eventDetails: { executionId: execution.id, clientId: viewedClientId, source: viewSource },
    })
  }, [panel, viewedClientId, viewSource, execution.id, trackingClient])

  // Results carry only a client id; the client details are loaded for whatever the page shows.
  const fetchMissingClients = useFetchMissingCrossClientClients()
  const { retryToken: clientLookupRetry, retryLater: retryClientLookup } = useRetryLater()
  const shownClientIds = Array.from(
    new Set(
      [...CCQ_CARD_ORDER.flatMap((card) => previews[card]), ...cursor.list.read.results, ...(panelResult ? [panelResult] : [])].map(
        (result) => result.clientId,
      ),
    ),
  ).join(',')
  useEffect(() => {
    if (shownClientIds === '') {
      return
    }
    fetchMissingClients(shownClientIds.split(',')).catch((e) => {
      console.error(e)
      retryClientLookup()
    })
  }, [shownClientIds, fetchMissingClients, clientLookupRetry, retryClientLookup])

  // Refetches the previews, so clients who moved out of a card stop showing in it.
  const settle = useCallback(() => {
    setSettleToken((previous) => previous + 1)
  }, [])

  const cancelPanelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  useEffect(() => cancelPanelClose, [cancelPanelClose])

  // The URL is replaced rather than pushed, so Back still leaves the page.
  const urlParams = useMemo(() => paramsFromCursorState(cursor.state), [cursor.state])
  useEffect(() => {
    if (Object.entries(urlParams).every(([param, value]) => searchParams.get(param) === value)) {
      return
    }
    const next = new URLSearchParams(searchParams)
    Object.entries(urlParams).forEach(([param, value]) => {
      if (value === null) {
        next.delete(param)
      } else {
        next.set(param, value)
      }
    })
    setSearchParams(next, { replace: true, state: location.state })
  }, [urlParams, searchParams, setSearchParams, location.state])

  // A closing card lets its moved clients go, except while the panel's arrows still step through them.
  const toggleCard = useCallback(
    (card: CcqCard) => {
      if (panel === null && openCard !== null) {
        settle()
      }
      setOpenCard((previous) => (previous === card ? null : card))
    },
    [panel, openCard, settle],
  )

  const { open: openInCursor } = cursor
  const open = useCallback(
    (resultId: string, card: CcqCard) => {
      cancelPanelClose()
      setIsPanelClosing(false)
      setLeaving(null)
      panelOpenerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      panelFocusRequestRef.current = true
      openInCursor(resultId, card)
    },
    [cancelPanelClose, openInCursor],
  )

  const closePanel = useCallback(() => {
    cancelPanelClose()
    setLeaving(selected)
    setIsPanelClosing(true)
    closeInCursor()
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      setIsPanelClosing(false)
      setLeaving(null)
      settle()
      // The row that opened it may have moved out of its card since.
      const opener = panelOpenerRef.current
      if (opener?.isConnected) {
        opener.focus()
      } else if (selected !== null) {
        document.querySelector<HTMLElement>(ccqCardHeadSelector(selected.card))?.focus()
      }
      panelOpenerRef.current = null
    }, CCQ_PANEL_EXIT_MS)
  }, [cancelPanelClose, selected, closeInCursor, settle])

  const { card: listCard, close: closeListInCursor } = cursor.list
  const closeList = useCallback(() => {
    if (listCard === null) {
      return
    }
    listCloseFocusRef.current = listCard
    // A client of the list the Preview doesn't hold closes with it; the Preview it may have left is read again.
    if (selected !== null && selected.card === listCard && !previews[listCard].some((result) => result.id === selected.resultId)) {
      settle()
    }
    closeListInCursor()
  }, [listCard, selected, previews, settle, closeListInCursor])

  const cursorValue = useMemo<CcqListCursor>(
    () => ({
      ...cursor,
      open,
      list: { ...cursor.list, close: closeList },
      details: { ...cursor.details, close: closePanel },
    }),
    [cursor, open, closeList, closePanel],
  )

  // Only for moves made this visit: results don't say which card the check put a client in, so after a reload it's unknown.
  // A move back to the card the check chose clears it.
  const movedFrom = useCallback(
    (result: CrossClientQueryResult): CcqCard | null => {
      const move = moves[result.id]
      return move === undefined || move.current === move.original ? null : move.original
    },
    [moves],
  )

  const toggleSelected = useCallback((resultId: string) => {
    setSelection((previous) => (previous.includes(resultId) ? previous.filter((id) => id !== resultId) : [...previous, resultId]))
  }, [])

  const deselect = useCallback((resultIds: string[]) => {
    setSelection((previous) => previous.filter((id) => !resultIds.includes(id)))
  }, [])
  useCcqDeselectUnlistedOnActions(selection, hasActionsFor(cursor.list.filter), deselect)

  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const accountId = useAppSelector(selectAccountId)
  const [changeMatch] = useChangeCrossClientQueryResultMatchMutation()
  const { refreshAfterResultMutation } = useCrossClientResultsRefresh()
  const { skip: skipInCursor, moveSettled } = cursor.details
  // The row leaves its card and the counts shift straight away; the panel moves on to the next client, or closes after the last.
  // Read through a ref, so it keeps its identity while the counts poll.
  const moveClientRef = useLatest(async (result: CrossClientQueryResult, match: CcqChangeableMatch) => {
    const from = cardForResult(result)
    const to = cardForResult({ status: CrossClientQueryResultStatuses.SUCCEEDED, match })
    if (to === null || from === to) {
      return
    }
    const name = getUserDisplayName(selectCrossClientClients(store.getState())[result.clientId])
    const save = () => changeMatch({ accountId, executionId: execution.id, resultId: result.id, match }).unwrap()
    // Not in a card (it failed, say): there's no row to take out, so the change is just saved and read back.
    if (from === null) {
      try {
        await save()
      } catch (e) {
        console.error(e)
        dispatch(addToast({ variant: 'error', title: ccqMoveFailed(name) }))
        return
      }
      deselect([result.id])
      dispatch(addToast({ variant: 'success', title: ccqMovedToast(name, to), delay: MOVED_TOAST_MS }))
      settle()
      refreshAfterResultMutation().catch(console.error)
      return
    }
    const previousMove = moves[result.id]
    const wasSelected = selection.includes(result.id)
    const fromBucket = { status: result.status, match: result.match ?? null, lastResolverStage: result.lastResolverStage ?? null }
    const toBucket = { status: CrossClientQueryResultStatuses.SUCCEEDED, match, lastResolverStage: result.lastResolverStage ?? null }
    if (selected?.resultId === result.id) {
      if (skipInCursor()) {
        panelFocusRequestRef.current = true
      } else {
        closePanel()
      }
    }
    setMoves((previous) => ({
      ...previous,
      [result.id]: {
        original: previousMove?.original ?? from,
        current: to,
        left: [...(previous[result.id]?.left ?? []).filter((card) => card !== to && card !== from), from],
      },
    }))
    deselect([result.id])
    dispatch(holdCrossClientResultBuckets(moveInBuckets(selectCrossClientResultBuckets(store.getState()), fromBucket, toBucket)))
    try {
      await save()
    } catch (e) {
      console.error(e)
      setMoves((previous) => (previousMove === undefined ? withoutKey(previous, result.id) : { ...previous, [result.id]: previousMove }))
      if (wasSelected) {
        setSelection((previous) => (previous.includes(result.id) ? previous : [...previous, result.id]))
      }
      dispatch(releaseCrossClientResultBuckets(moveInBuckets(selectCrossClientResultBuckets(store.getState()), toBucket, fromBucket)))
      moveSettled()
      dispatch(addToast({ variant: 'error', title: ccqMoveFailed(name) }))
      refreshAfterResultMutation().catch(console.error)
      return
    }
    dispatch(releaseCrossClientResultBuckets())
    moveSettled()
    dispatch(addToast({ variant: 'success', title: ccqMovedToast(name, to), delay: MOVED_TOAST_MS }))
    settle()
    refreshAfterResultMutation().catch(console.error)
  })
  const moveClient = useCallback((result: CrossClientQueryResult, match: CcqChangeableMatch) => moveClientRef.current(result, match), [moveClientRef])

  const clearSelection = useCallback(() => {
    setSelection([])
  }, [])

  const takePanelFocusRequest = useCallback(() => {
    const isRequested = panelFocusRequestRef.current
    panelFocusRequestRef.current = false
    return isRequested
  }, [])

  const takeListCloseFocus = useCallback((card?: CcqCard) => {
    const isRequested = listCloseFocusRef.current !== null && (card === undefined || listCloseFocusRef.current === card)
    if (isRequested) {
      listCloseFocusRef.current = null
    }
    return isRequested
  }, [])

  const value = useMemo<CcqWorkspace>(
    () => ({
      expanded,
      toggleCard,
      previews,
      previewsLoaded,
      previewsFailed,
      retryPreviews,
      panel,
      panelResult,
      isPanelClosing,
      moveClient,
      movedFrom,
      refreshPreviews: settle,
      selection,
      toggleSelected,
      deselect,
      setSelection,
      clearSelection,
      takePanelFocusRequest,
      takeListCloseFocus,
    }),
    [
      expanded,
      toggleCard,
      previews,
      previewsLoaded,
      previewsFailed,
      retryPreviews,
      panel,
      panelResult,
      isPanelClosing,
      moveClient,
      movedFrom,
      settle,
      selection,
      toggleSelected,
      deselect,
      clearSelection,
      takePanelFocusRequest,
      takeListCloseFocus,
    ],
  )

  return (
    <CcqWorkspaceContext.Provider value={value}>
      <CcqListCursorContext.Provider value={cursorValue}>{children}</CcqListCursorContext.Provider>
    </CcqWorkspaceContext.Provider>
  )
}
